import React, { useState, useEffect, useCallback } from "react";
import { Plus, Search, Loader2, AlertCircle, Trash2, X, Mail, Briefcase, Building2, Quote } from "lucide-react";
import { Star } from "lucide-react";
import TestimonialCard from "../components/TestimonialCard";
import { getInitials, getAvatarColor, formatDate, StarRating } from "../components/TestimonialCard";
import AddTestimonialModal from "../components/AddTestimonialModal";
import { adminService } from "../services/adminService";

// ── View Testimonial Modal ────────────────────────────────────────────────────
const STATUS_STYLES = {
  approved: "bg-emerald-50 text-emerald-600 border-emerald-200",
  rejected: "bg-red-50 text-red-500 border-red-200",
  pending:  "bg-amber-50 text-amber-600 border-amber-200",
};

const ViewTestimonialModal = ({ testimonial, onClose }) => {
  if (!testimonial) return null;
  const { full_name, email, job_title, company, rating = 5, content, status, is_featured, is_active, created_at, updatedAt } = testimonial;
  const bgColor = getAvatarColor(full_name);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg z-10 flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-surface-border flex items-start justify-between gap-3 flex-shrink-0">
          <h2 className="text-lg font-bold text-brand-text">Testimonial Details</h2>
          <button onClick={onClose} className="text-brand-light hover:text-brand-text transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 overflow-y-auto flex flex-col gap-5">
          {/* Avatar + name */}
          <div className="flex items-center gap-4">
            <div
              className="w-14 h-14 rounded-full flex items-center justify-center text-white text-lg font-bold flex-shrink-0"
              style={{ backgroundColor: bgColor }}
            >
              {getInitials(full_name)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-base font-bold text-brand-text">{full_name}</p>
                {is_featured && (
                  <span className="text-[10px] font-semibold bg-amber-100 text-amber-600 px-2 py-0.5 rounded-full">Featured</span>
                )}
                {status && (
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border capitalize ${STATUS_STYLES[status] ?? STATUS_STYLES.pending}`}>
                    {status}
                  </span>
                )}
              </div>
              <StarRating rating={rating} />
            </div>
          </div>

          {/* Info grid */}
          <div className="grid grid-cols-2 gap-3">
            {email && (
              <div className="flex items-center gap-2 text-sm text-brand-muted">
                <Mail className="w-4 h-4 text-brand-light flex-shrink-0" />
                <span className="truncate">{email}</span>
              </div>
            )}
            <div className="flex items-center gap-2 text-sm text-brand-muted">
              <Briefcase className="w-4 h-4 text-brand-light flex-shrink-0" />
              <span className="truncate">{job_title}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-brand-muted">
              <Building2 className="w-4 h-4 text-brand-light flex-shrink-0" />
              <span className="truncate">{company}</span>
            </div>
          </div>

          {/* Content */}
          <div className="bg-gray-50 rounded-xl p-4">
            <Quote className="w-5 h-5 text-blue-200 mb-2" />
            <p className="text-sm text-brand-text leading-relaxed whitespace-pre-wrap">{content}</p>
          </div>

          {/* Meta */}
          <div className="flex flex-wrap gap-4 text-xs text-brand-light border-t border-surface-border pt-4">
            {created_at && <span>Added: {formatDate(created_at)}</span>}
            {updatedAt  && <span>Updated: {formatDate(updatedAt)}</span>}
            <span>Active: {is_active ? "Yes" : "No"}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Confirm Delete Modal ──────────────────────────────────────────────────────
const ConfirmDeleteModal = ({ testimonial, onConfirm, onCancel, deleting }) => {
  if (!testimonial) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onCancel} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-sm z-10 p-6 flex flex-col gap-4">
        <div className="flex items-center justify-center w-12 h-12 rounded-full bg-red-50 mx-auto">
          <Trash2 className="w-5 h-5 text-danger" />
        </div>
        <div className="text-center">
          <h3 className="text-base font-bold text-brand-text">Delete Testimonial</h3>
          <p className="text-sm text-brand-muted mt-1">
            Are you sure you want to delete the testimonial by{" "}
            <span className="font-semibold text-brand-text">{testimonial.full_name}</span>?
            This action cannot be undone.
          </p>
        </div>
        <div className="flex gap-3 mt-1">
          <button
            onClick={onCancel}
            disabled={deleting}
            className="flex-1 py-2.5 rounded-xl border border-surface-border text-sm font-semibold text-brand-text hover:bg-surface transition-colors disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={deleting}
            className="flex-1 py-2.5 rounded-xl bg-danger text-white text-sm font-semibold hover:bg-red-600 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {deleting && <Loader2 className="w-4 h-4 animate-spin" />}
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Empty state ───────────────────────────────────────────────────────────────
const EmptyState = ({ query, onAdd }) => (
  <div className="bg-white border border-surface-border rounded-2xl p-14 flex flex-col items-center justify-center text-center gap-3">
    <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center">
      <Star className="w-6 h-6 text-amber-400" />
    </div>
    <p className="text-base font-semibold text-brand-text">
      {query ? "No testimonials found" : "No testimonials yet"}
    </p>
    <p className="text-sm text-brand-muted max-w-xs">
      {query
        ? `No results for "${query}". Try a different search.`
        : "Start collecting customer reviews and social proof."}
    </p>
    {!query && (
      <button
        onClick={onAdd}
        className="mt-1 inline-flex items-center gap-1.5 px-4 py-2 bg-primary text-white text-sm font-semibold rounded-lg hover:bg-primary-dark transition-colors"
      >
        <Plus className="w-4 h-4" /> Add First Testimonial
      </button>
    )}
  </div>
);

// ── Main Page ─────────────────────────────────────────────────────────────────
const AdminTestimonialsPage = () => {
  const [items, setItems]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState("");
  const [saving, setSaving]         = useState(false);
  const [query, setQuery]           = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [modalOpen, setModalOpen]   = useState(false);
  const [editing, setEditing]       = useState(null);
  const [viewing, setViewing]       = useState(null);
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [deleting, setDeleting]     = useState(false);

  // ── Fetch ────────────────────────────────────────────────────────────────
  const fetchTestimonials = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await adminService.getTestimonials({ limit: 100 });
      const list = res?.data?.testimonials ?? res?.data ?? [];
      setItems(Array.isArray(list) ? list : []);
    } catch (err) {
      setError(err.message || "Failed to load testimonials.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTestimonials(); }, [fetchTestimonials]);

  // ── Search (client-side, triggered on Enter) ────────────────────────────
  const filtered = searchQuery
    ? items.filter((t) => {
        const q = searchQuery.toLowerCase();
        return (
          (t.full_name  || "").toLowerCase().includes(q) ||
          (t.company    || "").toLowerCase().includes(q) ||
          (t.job_title  || "").toLowerCase().includes(q) ||
          (t.content    || "").toLowerCase().includes(q)
        );
      })
    : items;

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      setSearchQuery(query.trim());
    }
  };

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    if (val === "") setSearchQuery("");
  };

  const handleSearchClear = () => {
    setQuery("");
    setSearchQuery("");
  };

  // ── Add / Edit ────────────────────────────────────────────────────────────
  const openAdd  = () => { setEditing(null); setModalOpen(true); };
  const openEdit = (t) => { setEditing(t);   setModalOpen(true); };

  const handleSubmit = async (form) => {
    setSaving(true);
    setError("");
    try {
      if (editing) {
        const payload = {
          ...(editing.user_id ? { user_id: editing.user_id } : {}),
          full_name:        form.full_name            || "",
          ...(form.email?.trim() ? { email: form.email.trim() } : {}),
          job_title:        form.job_title            || "",
          company:          form.company             || "",
          rating:           form.rating,
          content:          form.content             || "",
          status:           editing.status           || "pending",
          is_featured:      editing.is_featured      ?? false,
          is_active:        editing.is_active        ?? true,
          rejection_reason: editing.rejection_reason || "",
        };
        await adminService.updateTestimonial(editing.id, payload);
      } else {
        const createPayload = {
          ...form,
          ...(form.email?.trim() ? { email: form.email.trim() } : { email: undefined }),
        };
        await adminService.createTestimonial(createPayload);
      }
      setModalOpen(false);
      fetchTestimonials();
    } catch (err) {
      setError(err.message || "Failed to save testimonial.");
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDelete       = (t) => setConfirmTarget(t);
  const handleCancelDelete = () => { if (!deleting) setConfirmTarget(null); };
  const handleConfirmDelete = async () => {
    if (!confirmTarget) return;
    setDeleting(true);
    try {
      await adminService.deleteTestimonial(confirmTarget.id);
      setItems((prev) => prev.filter((item) => item.id !== confirmTarget.id));
      setConfirmTarget(null);
    } catch (err) {
      setError(err.message || "Failed to delete testimonial.");
      setConfirmTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  // ── Approve / reject ──────────────────────────────────────────────────────
  // Goes through the dedicated status endpoint so approved_by and approved_at
  // are recorded. That endpoint only matches rows with is_active = true, so a
  // deactivated testimonial comes back as "not found" — say so plainly rather
  // than leaving the admin staring at a generic failure.
  const handleStatusChange = async (t, status) => {
    setError("");
    try {
      await adminService.updateTestimonialStatus(t.id, { status });
      setItems((prev) => prev.map((item) => (item.id === t.id ? { ...item, status } : item)));
      setViewing((prev) => (prev && prev.id === t.id ? { ...prev, status } : prev));
    } catch (err) {
      setError(
        t.is_active === false
          ? `"${t.full_name}" is deactivated, so its status cannot be changed. Reactivate it first.`
          : err.message || "Failed to update status."
      );
    }
  };

  return (
    <div className="flex flex-col gap-5">
      {/* Page header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-brand-text">Testimonials</h1>
          <p className="text-sm text-brand-muted mt-0.5">
            Manage customer reviews and social proof across the platform
          </p>
        </div>
        <button
          onClick={openAdd}
          className="flex items-center gap-1.5 px-4 py-2.5 bg-primary text-white text-sm font-semibold rounded-xl hover:bg-primary-dark transition-colors flex-shrink-0"
        >
          <Plus className="w-4 h-4" />
          Add Testimonial
        </button>
      </div>

      {/* Search bar */}
      <div className="bg-white border border-surface-border rounded-xl px-4 py-3 flex items-center gap-3">
        <Search className="w-4 h-4 text-brand-light flex-shrink-0" />
        <input
          type="text"
          placeholder="Search by name, company, keyword..."
          value={query}
          onChange={handleSearchChange}
          onKeyDown={handleSearchKeyDown}
          className="flex-1 text-sm text-brand-text placeholder:text-brand-light bg-transparent focus:outline-none"
        />
        {query && (
          <button onClick={handleSearchClear} className="text-brand-light hover:text-brand-text text-lg leading-none">×</button>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
          <AlertCircle className="w-4 h-4 shrink-0" /> {error}
        </div>
      )}

      {/* Grid / loading / empty state */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 text-primary animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState query={searchQuery} onAdd={openAdd} />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {filtered.map((t) => (
            <TestimonialCard
              key={t.id}
              testimonial={t}
              onView={(t) => setViewing(t)}
              onEdit={openEdit}
              onDelete={handleDelete}
              onApprove={(t) => handleStatusChange(t, "approved")}
              onReject={(t) => handleStatusChange(t, "rejected")}
            />
          ))}
        </div>
      )}

      {/* View modal */}
      <ViewTestimonialModal
        testimonial={viewing}
        onClose={() => setViewing(null)}
      />

      {/* Confirm delete modal */}
      <ConfirmDeleteModal
        testimonial={confirmTarget}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        deleting={deleting}
      />

      {/* Add / Edit modal */}
      <AddTestimonialModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmit}
        loading={saving}
        initial={editing}
      />
    </div>
  );
};

export default AdminTestimonialsPage;
